import { EApplicantType } from '@if/core/types'
import { Grid, useTheme } from '@mui/material'
import { useGetCreditReport } from '@src/data/api/credit-application/credit-application-api'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useGetFlinksIncomesForApplicant,
  useGetFlinksIncomesForCoApplicant,
} from '../../../data/selectors/credit-selectors'
import { Constants, CreditApplication } from '../../../data/types'
import { CreditReport } from '../../../data/types/CreditReportSchema'
import ApplicantSummary from './applicantSummary'
import CreditReportComponent from './creditReport'
import IncomesTable from './incomesTable'

export interface RefreshReportError {
  applicantType: EApplicantType
  errorCode: string
}

interface Props {
  applicantCreditReport: CreditReport | null
  setApplicantCreditReport: (creditReport: CreditReport) => void
  coApplicantCreditReport: CreditReport | null
  setCoApplicantCreditReport: (creditReport: CreditReport) => void
  creditApplication: CreditApplication
  selectedTabIndex: number
  refreshReport: (applicant: EApplicantType) => void
  refreshReportError: RefreshReportError | null
  userCanRefreshCreditReport: boolean
  editDisabled: boolean
}

const ApplicantInformations = ({
  creditApplication,
  applicantCreditReport,
  setApplicantCreditReport,
  coApplicantCreditReport,
  setCoApplicantCreditReport,
  refreshReport,
  refreshReportError,
  selectedTabIndex,
  userCanRefreshCreditReport,
  editDisabled,
}: Props) => {
  const navigate = useNavigate()
  const selectedTab: EApplicantType = selectedTabIndex === 2 ? EApplicantType.CoApplicant : EApplicantType.Applicant
  const selectedApplicant =
    selectedTab === EApplicantType.Applicant ? creditApplication?.applicant : creditApplication?.coApplicant
  const externalReportedIncomesForApplicant = useGetFlinksIncomesForApplicant()
  const externalReportedIncomesForCoApplicant = useGetFlinksIncomesForCoApplicant()
  const shouldFetchApplicantCreditReport = selectedTab === EApplicantType.Applicant && !applicantCreditReport
  const shouldFetchCoApplicantCreditReport = selectedTab === EApplicantType.CoApplicant && !coApplicantCreditReport

  const decision =
    selectedTab === EApplicantType.Applicant
      ? (creditApplication.applicant.creditDecision?.decision ??
        creditApplication.applicant?.prequalificationDecision?.decision)
      : (creditApplication.coApplicant?.creditDecision?.decision ??
        creditApplication.coApplicant?.prequalificationDecision?.decision)

  const handleSelectedTab = (index: number) => {
    const { location } = window
    const updateSearch = new URLSearchParams(location.search)
    updateSearch.set('tabIndex', encodeURIComponent(index))
    const newUrl = `${location.pathname}?${updateSearch.toString()}`
    navigate(newUrl, { replace: true })
  }

  const [creditReportData] = useGetCreditReport(
    creditApplication?.financingProgramId,
    creditApplication?.id,
    selectedApplicant?.hardHitReport?.creditReportId,
  )

  // Sync fetched data to component state
  useEffect(() => {
    if (creditReportData?.creditReport) {
      if (selectedTab === EApplicantType.Applicant && shouldFetchApplicantCreditReport) {
        setApplicantCreditReport(creditReportData.creditReport as unknown as CreditReport)
      }
      if (selectedTab === EApplicantType.CoApplicant && shouldFetchCoApplicantCreditReport) {
        setCoApplicantCreditReport(creditReportData.creditReport as unknown as CreditReport)
      }
    }
  }, [
    creditReportData,
    selectedTab,
    shouldFetchApplicantCreditReport,
    shouldFetchCoApplicantCreditReport,
    setApplicantCreditReport,
    setCoApplicantCreditReport,
  ])

  const theme = useTheme()

  const incomes = (name?: EApplicantType) => {
    return name === EApplicantType.Applicant
      ? creditApplication.applicant.currentJobs
      : creditApplication.coApplicant!.currentJobs
  }

  const otherIncomes = (name?: EApplicantType) => {
    return name === EApplicantType.Applicant
      ? creditApplication.applicant.otherIncomes
      : creditApplication.coApplicant!.otherIncomes
  }

  const externalReportedIncome = (name?: EApplicantType) => {
    return name === EApplicantType.Applicant
      ? externalReportedIncomesForApplicant
      : externalReportedIncomesForCoApplicant
  }

  const previousJobs = (name?: EApplicantType) => {
    return name === EApplicantType.Applicant
      ? creditApplication.applicant.previousJobs
      : creditApplication.coApplicant!.previousJobs
  }

  const shoulDisplayIncome = Constants.financingProgramConfigs[creditApplication.financingProgramId].useIncomeSection

  return (
    <>
      <Grid container marginBottom={2}>
        <Grid
          border="1px solid"
          borderColor={theme.palette.divider}
          borderRadius={2}
          sx={[
            selectedTab === EApplicantType.Applicant
              ? {
                  backgroundColor: theme.palette.action.selected,
                }
              : {
                  backgroundColor: '',
                },
          ]}
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <ApplicantSummary
            applicant={creditApplication.applicant}
            creditApplicationId={creditApplication.id}
            financingProgramId={creditApplication.financingProgramId}
            onClickExpand={() => handleSelectedTab(1)}
            editDisabled={editDisabled}
          />
        </Grid>
        {creditApplication.coApplicant && (
          <Grid
            border="1px solid"
            borderColor={theme.palette.divider}
            borderRadius={2}
            sx={[
              selectedTab === EApplicantType.CoApplicant
                ? {
                    backgroundColor: theme.palette.action.selected,
                  }
                : {
                    backgroundColor: '',
                  },
            ]}
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <ApplicantSummary
              applicant={creditApplication.coApplicant}
              isCoapplicant
              creditApplicationId={creditApplication.id}
              financingProgramId={creditApplication.financingProgramId}
              onClickExpand={() => handleSelectedTab(2)}
              editDisabled={editDisabled}
            />
          </Grid>
        )}
      </Grid>
      {shoulDisplayIncome && (
        <IncomesTable
          incomes={incomes(selectedTab)}
          otherIncomes={otherIncomes(selectedTab)}
          externalReportedIncome={externalReportedIncome(selectedTab)?.annualAverageGrossIncome}
        />
      )}
      {decision && (
        <CreditReportComponent
          key={selectedTab}
          refreshReport={() => refreshReport(selectedTab)}
          refreshReportErrorCode={
            refreshReportError?.applicantType === selectedTab ? refreshReportError.errorCode : null
          }
          creditReport={selectedTab === EApplicantType.Applicant ? applicantCreditReport : coApplicantCreditReport}
          softHitReport={
            selectedTab === EApplicantType.Applicant
              ? creditApplication.applicant.softHitReport
              : creditApplication.coApplicant?.softHitReport
          }
          decision={decision}
          userCanRefreshCreditReport={userCanRefreshCreditReport}
          financingProgramId={creditApplication.financingProgramId}
          editDisabled={editDisabled}
          existingOnGoingLoans={
            selectedTab === EApplicantType.Applicant
              ? creditApplication.applicant.existingCustomerReference?.existingOnGoingLoans
              : creditApplication.coApplicant?.existingCustomerReference?.existingOnGoingLoans
          }
          previousJobs={previousJobs(selectedTab)}
        />
      )}
    </>
  )
}

export default ApplicantInformations
